import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Accordion,
  ListGroup,
  Button,
  Card,
  Form,
} from "react-bootstrap";
import {
  Zap,
  Users,
  ClipboardList,
  Layout,
  Clock,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Shield,
  Network,
  Target,
} from "lucide-react";
import axios from "axios";

/* -------------------------------------------------------------------------- */
/*                               API DATA (CLEANED & FIXED)                   */
/* -------------------------------------------------------------------------- */

const RAW_CHAPTERS = [
  {
    id: "812",
    course_id: null,
    course_url: "ethical-hacking-specialization",
    chapter_id: "1",
    chapter_title: "Introduction to ethical hacking and cyber security",
    topics: [
      { id: "1", topic_title: "What is a website", demo: "1", type: "Theory" },
      {
        id: "1792",
        topic_title: "Introduction to Ethical Hacking and Cyber Security",
        demo: "0",
      },
      {
        id: "1793",
        topic_title: "Career's in ethical hacking and cyber security",
        demo: "0",
      },
      { id: "1794", topic_title: "Types of hackers", demo: "0" },
      {
        id: "1798",
        topic_title: "Roadmap to become an ethical hacker",
        demo: "0",
      },
      {
        id: "1799",
        topic_title: "Steps followed by black hat hackers",
        demo: "0",
      },
      {
        id: "1800",
        topic_title: "Steps followed by white hat hackers",
        demo: "0",
      },
      {
        id: "1801",
        topic_title: "Introduction to penetration testing",
        demo: "0",
      },
      { id: "1802", topic_title: "Types of penetration testing", demo: "0" },
      { id: "1803", topic_title: "Fundamentals of Indian IT Act", demo: "0" },
      {
        id: "2311",
        topic_title: "IPC Sections, Penalties and Punishments for cyber crimes",
        demo: "0",
      },
      { id: "2312", topic_title: "Top 10 hackers and their skills", demo: "0" },
      {
        id: "2313",
        topic_title: "Top 10 cyber attacks the world has faced",
        demo: "0",
      },
      {
        id: "2766",
        topic_title: "Introduction to Ethical Hacking and Cyber Security",
        demo: "0",
      },
      {
        id: "2767",
        topic_title: "Career's in ethical hacking and cyber security",
        demo: "0",
      },
      { id: "2768", topic_title: "Types of hackers", demo: "0" },
      {
        id: "2769",
        topic_title: "Roadmap to become an ethical hacker",
        demo: "0",
      },
      {
        id: "2770",
        topic_title: "Steps followed by black hat hackers",
        demo: "0",
      },
      {
        id: "2771",
        topic_title: "Steps followed by white hat hackers",
        demo: "0",
      },
      {
        id: "2772",
        topic_title: "Introduction to penetration testing",
        demo: "0",
      },
      { id: "2773", topic_title: "Types of penetration testing", demo: "0" },
      { id: "2774", topic_title: "Fundamentals of Indian IT Act", demo: "0" },
      {
        id: "2775",
        topic_title: "IPC Sections, Penalties and Punishments for cyber crimes",
        demo: "0",
      },
      { id: "2776", topic_title: "Top 10 hackers and their skills", demo: "0" },
      {
        id: "2777",
        topic_title: "Top 10 cyber attacks the world has faced",
        demo: "0",
      },
    ],
  },
];

/* -------------------------------------------------------------------------- */
/*                               CURRICULUM BUILDER                           */
/* -------------------------------------------------------------------------- */
const useCurriculumData = (chapters) => {
  return useMemo(() => {
    const chapterMap = new Map();
    // console.log("chapters data in curriculum", chapters);
    chapters.forEach((ch) => {
      const key = ch.chapter_id;
      if (!chapterMap.has(key)) {
        const uniqueTopics = new Map();
        ch.topics.forEach((t) => {
          if (t.topic_title && !uniqueTopics.has(t.topic_title)) {
            uniqueTopics.set(t.topic_title, {
              id: t.id,
              name: t.topic_title,
              type: t.type === "Theory" ? classTypes[0] : classTypes[1],
            });
          }
        });

        chapterMap.set(key, {
          id: ch.id,
          chapter_id: ch.chapter_id,
          title: ch.chapter_title,
          topics: Array.from(uniqueTopics.values()),
        });
      }
    });

    const uniqueChapters = Array.from(chapterMap.values()).sort(
      (a, b) => parseInt(a.chapter_id) - parseInt(b.chapter_id)
    );

    const weekGroups = [];
    let week = 1;
    uniqueChapters.forEach((ch, idx) => {
      const span = ch.topics.length > 20 ? 2 : 1;
      weekGroups.push({
        groupId: `week-${week}-${week + span - 1}`,
        groupTitle: `Chapter - ${week}${span > 1 ? `–${week + span - 1}` : ""} `,
        groupDetails: `Chapter ${ch.chapter_id}: ${ch.title} • ${ch.topics.length} topics`,
        startWeek: week,
        endWeek: week + span - 1,
        courses: [
          {
            id: ch.chapter_id,
            title: ch.title,
            description: `${ch.topics.length} topics`,
            icon: "Target",
            config: colorConfigs[idx % 4],
            duration: `${span * 4}h`,
            classes: ch.topics,
          },
        ],
      });
      week += span;
    });

    return weekGroups;
  }, [chapters]);
};

/* -------------------------------------------------------------------------- */
/*                               ORIGINAL CONSTANTS                           */
/* -------------------------------------------------------------------------- */
const classTypes = [
  { name: "Theory", icon: BookOpen, color: "#3b82f6" },
  { name: "Lab / Practical", icon: Zap, color: "#10b981" },
  { name: "Assessment", icon: ClipboardList, color: "#ef4444" },
];

const colorConfigs = [
  { scheme: "red", bg: "#fff1f2", text: "#e11d48", marker: "#fda4af" },
  { scheme: "sky", bg: "#f0f9ff", text: "#0ea5e9", marker: "#7dd3fc" },
  { scheme: "emerald", bg: "#ecfdf5", text: "#059669", marker: "#6ee7b7" },
  { scheme: "gray", bg: "#f3f4f6", text: "#4b5563", marker: "#9ca3af" },
];

const IconMap = { Zap, Users, ClipboardList, Layout, Shield, Network, Target };

/* -------------------------------------------------------------------------- */
/*                               ORIGINAL HELPERS                             */
/* -------------------------------------------------------------------------- */
const getStyleConfig = (config, isOpen) => {
  const baseBg = config.bg;
  const baseText = config.text;
  const activeBg =
    config.scheme === "red"
      ? "#fee2e2"
      : config.scheme === "sky"
      ? "#e0f2fe"
      : config.scheme === "emerald"
      ? "#d1fae5"
      : "#e5e7eb";

  return {
    iconContainer: {
      backgroundColor: isOpen ? activeBg : baseBg,
      color: baseText,
      padding: "0.75rem",
      borderRadius: "0.75rem",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "48px",
      minHeight: "48px",
      transition: "background-color 0.2s ease",
    },
    listItemMarker: config.marker,
  };
};

/* -------------------------------------------------------------------------- */
/*                               ORIGINAL COMPONENTS                          */
/* -------------------------------------------------------------------------- */
const WeekNavigation = ({ data, activeGroup, setActiveGroup }) => {
  const MobileHorizontalNav = (
    <div
      className="d-block d-md-none border-bottom bg-white sticky-top"
      style={{ top: 0, zIndex: 1020 }}
    >
      <div
        className="d-flex overflow-auto py-3 px-3"
        style={{ WebkitOverflowScrolling: "none", scrollbarWidth: "none" }}
      >
        {data.map((group) => {
          const isActive = activeGroup === group.groupId;
          return (
            <Button
              key={group.groupId}
              variant="link"
              onClick={() => setActiveGroup(group.groupId)}
              className="text-decoration-none px-4 py-2 flex-shrink-0 position-relative me-3"
              style={{
                color: isActive ? "#3b82f6" : "#6b7280",
                fontWeight: isActive ? "600" : "500",
                transition: "color 0.2s",
              }}
            >
              {group.groupTitle.split(":")[0].trim()}
              {isActive && (
                <div
                  className="position-absolute bottom-0 start-0 w-100"
                  style={{
                    height: "3px",
                    backgroundColor: "#3b82f6",
                    borderRadius: "1.5px",
                  }}
                />
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );

  const DesktopSidebar = (
    <div
      className="d-none d-md-block vh-100"
      style={{
        position: "sticky",
        top: 0,
        backgroundColor: "#ffffff",
        overflowY: "auto",
        padding: "1.5rem 0",
      }}
    >
      <div className="px-4 py-2 mb-2">
        <h2 className="h6 text-dark fw-bold">Full Curriculum</h2>
      </div>
      <ListGroup variant="flush" className="p-0">
        {data.map((group) => {
          const isActive = activeGroup === group.groupId;
          return (
            <ListGroup.Item
              key={group.groupId}
              action
              onClick={() => setActiveGroup(group.groupId)}
              className={`px-0 py-1 border-0 rounded-0 position-relative cursor-pointer ${
                isActive ? "sidebar-active-item" : "sidebar-inactive-item"
              }`}
              style={{ transition: "all 0.2s ease" }}
            >
              {isActive && (
                <div
                  className="position-absolute start-0 top-0 bottom-0 rounded-end-sm"
                  style={{ width: "3px", backgroundColor: "#f9f9fa" }}
                />
              )}
              <div className="d-flex align-items-center gap-3 py-2 ps-4 pe-3">
                <BookOpen
                  size={16}
                  style={{ color: isActive ? "#10b981" : "#6b7280" }}
                />
                <span
                  className="flex-grow-1"
                  style={{
                    color: isActive ? "#111827" : "#374151",
                    fontWeight: isActive ? "600" : "500",
                    fontSize: "0.95rem",
                  }}
                >
                  {group.groupTitle}
                </span>
              </div>
            </ListGroup.Item>
          );
        })}
      </ListGroup>
    </div>
  );

  return (
    <>
      {MobileHorizontalNav}
      {DesktopSidebar}
    </>
  );
};

const DetailedClassCard = ({ classItem, markerColor }) => {
  const { name } = classItem;
  return (
    <div
      className="d-flex align-items-center bg-white p-3 rounded-3 shadow-sm class-card h-100"
      style={{ borderLeft: `4px solid ${markerColor}` }}
    >
      <span className="text-dark fw-medium flex-grow-1 text-start ms-1 mb-0 text-wrap">
        {name}
      </span>
    </div>
  );
};

const CourseContent = ({ activeGroupData }) => {
  const [activeCourseId, setActiveCourseId] = useState(null);

  useEffect(() => {
    if (!activeGroupData || !Array.isArray(activeGroupData.courses) || activeGroupData.courses.length === 0) {
      setActiveCourseId(null);
      return;
    }
    setActiveCourseId(activeGroupData.courses[0].id);
  }, [activeGroupData]);

  const toggleAccordion = useCallback((eventKey) => {
    setActiveCourseId((c) => (c === eventKey ? null : eventKey));
  }, []);

  if (!activeGroupData) {
    return (
      <div className="p-5 text-center">
        <h2 className="text-muted">Loading...</h2>
      </div>
    );
  }

  return (
    <div className="p-3 p-md-4 p-lg-5 w-100">
      <header className="mb-4">
        {/* <h1 className="h3  text-dark mb-1">
          {activeGroupData.groupTitle}
        </h1> */}
        <p className="text-mutedc fw-bolder mb-0">
          {activeGroupData.groupDetails}
        </p>
      </header>

      <Accordion
        activeKey={activeCourseId}
        onSelect={toggleAccordion}
        className="right-section"
      >
        {activeGroupData.courses.map((course) => {
          const Icon = IconMap[course.icon];
          const isOpen = activeCourseId === course.id;
          const styles = getStyleConfig(course.config, isOpen);

          return (
            <Card key={course.id} className="border-0 rounded-4 mb-3">
              <Accordion.Item eventKey={course.id} className="border-0">
                <Accordion.Header as="div" className="p-0 border-0">
                  <div
                    className="w-100 text-decoration-none accordion-section text-start px-3 py-3 d-flex justify-content-between align-items-center bg-white rounded-4 shadow-sm"
                    style={{
                      boxShadow: isOpen
                        ? "0 4px 12px rgba(0, 0, 0, 0.08)"
                        : "0 1px 3px rgba(0, 0, 0, 0.05)",
                      transition: "box-shadow 0.2s ease",
                      cursor: "pointer",
                    }}
                  >
                    <div className="d-flex align-items-start gap-3 flex-grow-1">
                      <div style={styles.iconContainer}>
                        <Icon size={17} />
                      </div>
                      <div className="pt-1">
                        <strong className="mb-0 text-dark h6">
                          {course.title}
                        </strong>
                      </div>
                    </div>
                    <div className="d-none d-sm-flex align-items-center gap-3 ms-3">
                      <span className="d-flex align-items-center small fw-medium text-secondary bg-light px-3 py-1 rounded-pill">
                        <Clock size={13} className="me-1" />
                        {course.duration}
                      </span>
                      {isOpen ? (
                        <ChevronUp size={20} className="text-muted" />
                      ) : (
                        <ChevronDown size={20} className="text-muted" />
                      )}
                    </div>
                  </div>
                </Accordion.Header>

                <Accordion.Body className="pt-0 pb-4 px-2 px-md-4">
                  <div className="border-top pt-4 mt-3">
                    <p className="text-muted small mb-3">
                      {course.description}
                    </p>
                    <h4 className="h6 fw-bold text-dark text-start mb-3">
                      Topics:
                    </h4>
                    <Row className="g-3">
                      {course.classes.map((cls) => (
                        <Col xs={12} sm={6} lg={6} key={cls.id}>
                          <DetailedClassCard
                            classItem={cls}
                            markerColor={course.config.marker}
                          />
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Card>
          );
        })}
      </Accordion>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                               MAIN COMPONENT                               */
/* -------------------------------------------------------------------------- */

export default function HackingCoursecurriculum() {
  const [chapters, setChapters] = useState(RAW_CHAPTERS);

  const curriculumData = useCurriculumData(Array.isArray(chapters) ? chapters : []);
  const [intrenshipCourses, setIntrenshipCourses] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);

  useEffect(() => {
    if (Array.isArray(curriculumData) && curriculumData.length > 0) {
      setActiveGroup(curriculumData[0].groupId);
    } else {
      setActiveGroup(null);
    }
  }, [curriculumData]);

  const activeGroupData = useMemo(function () {
    if (!Array.isArray(curriculumData)) return null;
    for (var i = 0; i < curriculumData.length; i++) {
      if (curriculumData[i].groupId === activeGroup) return curriculumData[i];
    }
    return null;
  }, [activeGroup, curriculumData]);

  const doodleStyle = {
    position: "absolute",
    inset: 0,
    zIndex: 0,
    opacity: 0.1,
    overflow: "hidden",
    backgroundColor: "#f9f9fa",
  };
  const getIntrenshipCourses = async (e) => {
    // e.preventDefault();

    const data = new FormData();
    data.append("course_url", "ethical-hacking-specialization");

    const res = await axios.post(
      "https://www.skillsuprise.com/get-internship-course.php",
      data,
      { headers: { "content-type": "multipart/FormData" } }
    );

    if (res) {
      setChapters(res.data);
      console.log("intrenship courses data", res.data);
    }
  };
  useEffect(() => {
    getIntrenshipCourses();
  }, []);
  return (
    <div
      style={{
        minHeight: "100vh",
        fontFamily:
          "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
    

      <style>{`
        .sidebar-inactive-item:hover { background-color:#f4f4f5 !important; }
        .sidebar-active-item { background-color:#f0fdf4 !important; }
        .accordion-button:not(.collapsed)::after,
        .accordion-button::after { background-image:none !important; height:0 !important; }
        .accordion-button:focus,
        .accordion-button:not(.collapsed) { box-shadow:none !important; background-color:transparent !important; color:inherit !important; }
        .accordion-button { padding:0 !important; font-size:1rem !important; }
        .accordion-item { background-color:transparent !important; }
        .class-card { box-shadow:0 1px 2px rgba(0,0,0,0.06); transition:transform .2s ease,box-shadow .2s ease; }
        .class-card:hover { transform:translateY(-2px); box-shadow:0 4px 8px rgba(0,0,0,0.08); }
        .content-column { height:100vh; overflow-y:auto; }
      `}</style>

      <Container fluid className="p-0 position-relative" style={{ zIndex: 1 }}>
        <Row className="g-0">
          <Col xs={12} md={4} lg={3} xl={2} className="bg-white shadow-sm">
            <WeekNavigation
              data={curriculumData}
              activeGroup={activeGroup}
              setActiveGroup={setActiveGroup}
            />
          </Col>
          <Col
            xs={12}
            md={8}
            lg={9}
            xl={10}
            className="content-column"
            style={{ backgroundColor: "#f9f9fa" }}
          >
            <CourseContent activeGroupData={activeGroupData} />
          </Col>
        </Row>
      </Container>
    </div>
  );
}
